import { ColDef, ICellRendererParams, ITooltipParams } from 'ag-grid-community';
import { ChevronRight } from '@mui/icons-material';
import { CommodityManagementApi } from '../../../../../Apis/Apis';
import { StoneXGrid, StoneXRow } from '../../../../StoneX';
import { StoneXFileUploader } from '../../../../StoneXMui';
import { useEffect, useState } from 'react';
import { Button, IconButton, LinearProgress, Box, Typography } from '@mui/material';
import { PositionPricingComponentModel, CommodityModel, GetCustomerDataResponse, GetCustomerSettingsResponse, LocationModel, CounterpartyModel, BudgetModel, UpdatePositionModel, BudgetPricingComponentModel, ContractDate, PositionPricingStrategyModel, UpdateDealTicketRequest, Side, PositionType, OptionType, OptionStyle, Unit, Currency } from '../../../../../Generated/Commodity-Management-Api';
import { Constants } from '../../../../../Core/Constants';
import { RootState, useApplicationSelector } from '../../../../../Redux/ReduxStore';
import { filter } from 'lodash';
import * as XLSX from 'xlsx';
import { useSnackbar } from "notistack";
import { UploadPositionExcelRow } from './UploadPositionExcelRow';

interface UploadPositionsProps {
    pricingStrategies?: PositionPricingComponentModel[];
}

interface UploadProgress {
    current: number;
    total: number;
    status: 'idle' | 'validating' | 'uploading' | 'complete' | 'error';
    message: string;
}

export default function UploadPositions(props: UploadPositionsProps) {

    const { pricingStrategies } = props;

    const { enqueueSnackbar } = useSnackbar();

    const customerData = useApplicationSelector((state: RootState): GetCustomerDataResponse => state.commodityManagement.customerData);
    const customerSettings = useApplicationSelector((state: RootState): GetCustomerSettingsResponse => state.commodityManagement.customerSettings);

    const [expanded, setExpanded] = useState(false);
    const [invalidCommoditiesWithRow, setInvalidCommoditiesWithRow] = useState<{ type: string; name: string; row: number }[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<UploadProgress>({
        current: 0,
        total: 0,
        status: 'idle',
        message: ''
    });

    function resetProgress() {
        setUploadProgress({ current: 0, total: 0, status: 'idle', message: '' });
    }

    // Safely lowercase any value coming from XLSX (which may return numbers, Dates, etc.)
    const lc = (v: unknown): string => (v === null || v === undefined ? '' : String(v).toLowerCase());

    function mapRowToUpdatePositionModel(row: any): UploadPositionExcelRow {
        // Helper to get value from row, checking both with and without '*'
        const getValue = (key: string) => row[key] ?? row[`${key}*`];

        const commodity = (customerData.commodities ?? []).find(
            (c: any) => lc(c.commodityName) === lc(getValue('Commodity'))
        );
        const location = (customerData.locations ?? []).find(
            (c: any) => lc(c.locationName) === lc(getValue('Location'))
        );
        const counterparty = (customerData.counterparties ?? []).find(
            (c: any) => lc(c.counterpartyName) === lc(getValue('Counterparty'))
        );
        const portfolio = (customerData.portfolios ?? []).find(
            (c: any) => lc(c.portfolioName) === lc(getValue('Portfolio'))
        );

        const tradeDate = getValue('Trade Date');

        return {
            dealTicketId: 0,
            commodityId: commodity?.commodityId,
            counterpartyId: counterparty?.counterpartyId,
            locationId: location?.locationId,
            portfolioId: portfolio?.portfolioId,
            budgetDate: { year: getValue('Budget Year'), month: getValue('Budget Month') } as ContractDate,
            contractDate: { year: getValue('Contract Year'), month: getValue('Contract Month') } as ContractDate,
            volume: getValue('Volume'),
            unit: getValue('Unit'),
            currency: getValue('Currency'),
            price: getValue('Price'),
            pricingStrategyId: 0,
            positionType: getValue('Position Type'),
            optionStrike: getValue('Option Strike'),
            optionType: getValue('Option Type'),
            optionStyle: getValue('Option Style'),
            side: getValue('Side'),
            tradeDate: tradeDate
                ? typeof tradeDate === "number"
                    ? new Date(Math.round((tradeDate - 25569) * 86400 * 1000)).toISOString().substring(0, 10)
                    : new Date(tradeDate).toISOString().substring(0, 10)
                : undefined,
            dealTicketNumber: getValue('Deal Ticket'),
            account: getValue('Account'),
            symbol: getValue('Symbol')
        } as UploadPositionExcelRow;
    }

    function getPositionsFromUpload(file: File) {
        resetProgress();
        setUploadProgress(prev => ({ ...prev, status: 'validating', message: 'Reading file...' }));

        const validCommodityNames = (customerData.commodities ?? []).map((c: any) => c.commodityName);
        const validCounterpartyNames = (customerData.counterparties ?? []).map((c: any) => c.counterpartyName);
        const validLocationNames = (customerData.locations ?? []).map((c: any) => c.locationName);

        const requiredHeaders = [
            "Commodity",
            "Counterparty",
            "Location",
            "Account",
            "Portfolio",
            "Trade Date",
            "Deal Ticket",
            "Budget Month",
            "Budget Year",
            "Contract Month",
            "Contract Year",
            "Side",
            "Position Type",
            "Volume",
            "Unit",
            "Currency",
            "Price",
            "Option Strke",
            "Option Type",
            "Option Style",
            "Symbol"
        ];

        const reader = new FileReader();
        reader.onload = (event) => {
            // Helper to get value from row, checking both with and without '*'
            const getValue = (row: any, key: string) => row[key] ?? row[`${key}*`];

            const data = new Uint8Array(event.target?.result as ArrayBuffer);
            const workbook = XLSX.read(data, { type: 'array' });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];

            // Get headers from the first row
            const headerRow: string[] = XLSX.utils.sheet_to_json(worksheet, { header: 1 })[0] as string[] || [];
            // Remove '*' from requiredHeaders before comparing
            const normalizedRequiredHeaders = requiredHeaders.map(h => String(h ?? '').replace(/\*/g, ''));
            const normalizedHeaderRow = headerRow.map(h => String(h ?? '').replace(/\*/g, ''));
            const missingHeaders = normalizedRequiredHeaders.filter(h => !normalizedHeaderRow.includes(h));
            if (missingHeaders.length > 0) {
                setInvalidCommoditiesWithRow([
                    { type: "Header", name: `Missing: ${missingHeaders.join(", ")}`, row: 1 }
                ]);
                setUploadProgress({ current: 0, total: 0, status: 'error', message: 'Missing required headers' });
                console.warn("Missing required headers:", missingHeaders);
                return;
            }

            const rows: any[] = XLSX.utils.sheet_to_json(worksheet, { header: 0 });
            setUploadProgress(prev => ({ ...prev, message: `Validating ${rows.length} rows...` }));

            const invalidCommoditiesSet = new Set<string>();
            const invalids: { type: string; name: string; row: number }[] = [];
            const updatePositionModels: UploadPositionExcelRow[] = [];

            rows.forEach((row, idx) => {
                const commodityVal = getValue(row, 'Commodity');
                const counterpartyVal = getValue(row, 'Counterparty');
                const locationVal = getValue(row, 'Location');
                const sideVal = getValue(row, 'Side');
                const positionTypeVal = getValue(row, 'Position Type');
                const optionTypeVal = getValue(row, 'Option Type');
                const optionStyleVal = getValue(row, 'Option Style');

                // Validate Commodity
                if (
                    commodityVal &&
                    !validCommodityNames.some(
                        (name) => lc(name) === lc(commodityVal)
                    )
                ) {
                    invalidCommoditiesSet.add(commodityVal + '_' + (idx + 2));
                    invalids.push({ type: "Commodity", name: commodityVal, row: idx + 2 });
                }
                // Validate Counterparty
                if (
                    counterpartyVal && customerSettings.positionsByCounterparty &&
                    !validCounterpartyNames.some(
                        (name) => lc(name) === lc(counterpartyVal)
                    )
                ) {
                    invalidCommoditiesSet.add(counterpartyVal + '_' + (idx + 2));
                    invalids.push({ type: "Counterparty", name: counterpartyVal, row: idx + 2 });
                }
                // Validate Location
                if (
                    locationVal && customerSettings.positionsByLocation &&
                    !validLocationNames.some(
                        (name) => lc(name) === lc(locationVal)
                    )
                ) {
                    invalidCommoditiesSet.add(locationVal + '_' + (idx + 2));
                    invalids.push({ type: "Location", name: locationVal, row: idx + 2 });
                }
                // Validate Side
                const validSides: string[] = Object.values(Side);
                if (
                    sideVal &&
                    !validSides.some(
                        (side) => lc(side) === lc(sideVal)
                    )
                ) {
                    invalids.push({ type: "Side", name: sideVal, row: idx + 2 });
                }
                // Validate Position Type
                const validPositionTypes: string[] = Object.values(PositionType);
                if (
                    positionTypeVal &&
                    !validPositionTypes.some(
                        (type) => lc(type) === lc(positionTypeVal)
                    )
                ) {
                    invalids.push({ type: "Position Type", name: positionTypeVal, row: idx + 2 });
                }
                // Validate Option Type
                const validOptionTypes: string[] = Object.values(OptionType);
                if (
                    optionTypeVal &&
                    !validOptionTypes.some(
                        (type) => lc(type) === lc(optionTypeVal)
                    )
                ) {
                    invalids.push({ type: "Option Type", name: optionTypeVal, row: idx + 2 });
                }
                // Validate Option Style
                const validOptionStyles: string[] = Object.values(OptionStyle);
                if (
                    optionStyleVal &&
                    !validOptionStyles.some(
                        (type) => lc(type) === lc(optionStyleVal)
                    )
                ) {
                    invalids.push({ type: "Option Style", name: optionStyleVal, row: idx + 2 });
                }

                if (
                    (!commodityVal || validCommodityNames.some((name) => lc(name) === lc(commodityVal))) &&
                    (!counterpartyVal || validCounterpartyNames.some((name) => lc(name) === lc(counterpartyVal))) &&
                    (!locationVal || validLocationNames.some((name) => lc(name) === lc(locationVal)))
                ) {
                    updatePositionModels.push(mapRowToUpdatePositionModel(row));
                }
            });
            setInvalidCommoditiesWithRow(invalids);

            if (invalids.length > 0) {
                setUploadProgress({ current: 0, total: 0, status: 'error', message: `Found ${invalids.length} validation error(s)` });
                console.warn("Invalid commodities found in upload:", invalids);
                return;
            }

            // Step 4: If all commodities are valid, proceed with API call
            savePositions(updatePositionModels);
        };
        reader.readAsArrayBuffer(file);
    }

    async function savePositions(p: UploadPositionExcelRow[]) {
        setIsSaving(true);

        // Group rows by dealTicketNumber
        const dealTicketMap = new Map<string, UploadPositionExcelRow[]>();
        p.forEach(row => {
            const dealTicketNumber = row.dealTicketNumber?.toString().trim() ?? '';
            if (!dealTicketNumber) return; // skip if no deal ticket number
            if (!dealTicketMap.has(dealTicketNumber)) {
                dealTicketMap.set(dealTicketNumber, []);
            }
            dealTicketMap.get(dealTicketNumber)!.push(row);
        });

        // Create an array of dealTickets, only one per unique dealTicketNumber
        const dealTickets = Array.from(dealTicketMap.entries()).map(([dealTicketNumber, rows]) => {
            const firstRow = rows[0];
            return {
                dealTicketNumber: dealTicketNumber,
                tradeDate: firstRow.tradeDate,
                commodityId: firstRow.commodityId,
                counterpartyId: firstRow.counterpartyId,
                portfolioId: firstRow.portfolioId,
                positions: rows.map(row => ({
                    commodityId: row.commodityId,
                    counterpartyId: row.counterpartyId,
                    locationId: row.locationId,
                    portfolioId: row.portfolioId,
                    budgetDate: row.budgetDate,
                    contractDate: row.contractDate,
                    volume: row.volume,
                    unit: row.unit,
                    currency: row.currency,
                    price: row.price,
                    positionType: row.positionType,
                    optionStrike: row.optionStrike,
                    optionType: row.optionType,
                    optionStyle: row.optionStyle,
                    side: row.side,
                    tradeDate: row.tradeDate,
                    dealTicketNumber: row.dealTicketNumber,
                    account: row.account,
                    symbol: row.symbol,
                } as UpdatePositionModel))
            } as UpdateDealTicketRequest;
        });

        const totalTickets = dealTickets.length;
        setUploadProgress({
            current: 0,
            total: totalTickets,
            status: 'uploading',
            message: `Uploading 0 of ${totalTickets} deal tickets...`
        });

        try {
            for (let i = 0; i < dealTickets.length; i++) {
                const dealTicket = dealTickets[i];
                await CommodityManagementApi.addTrade(null, dealTicket);
                
                setUploadProgress({
                    current: i + 1,
                    total: totalTickets,
                    status: 'uploading',
                    message: `Uploading ${i + 1} of ${totalTickets} deal tickets...`
                });
            }
            setUploadProgress({
                current: totalTickets,
                total: totalTickets,
                status: 'complete',
                message: `Successfully uploaded ${totalTickets} deal ticket(s)`
            });
            enqueueSnackbar('All deal tickets were successfully added', { variant: 'success' });
        } catch (err) {
            setUploadProgress(prev => ({
                ...prev,
                status: 'error',
                message: `Error uploading deal tickets (${prev.current} of ${prev.total} completed)`
            }));
            enqueueSnackbar('An error occurred while saving the deal tickets', { variant: 'error' });
        } finally {
            setIsSaving(false);
        }
    }

    function downloadTemplate() {
        const requiredHeaders = [
            "Commodity*",
            customerSettings.positionsByCounterparty ? "Counterparty*" : "Counterparty",
            customerSettings.positionsByLocation ? "Location*" : "Location",
            "Account",
            "Portfolio",
            "Trade Date*",
            "Deal Ticket*",
            "Budget Month*",
            "Budget Year*",
            "Contract Month*",
            "Contract Year*",
            "Side*",
            "Position Type*",
            "Volume*",
            "Unit*",
            "Currency*",
            "Price*",
            "Option Strke",
            "Option Type",
            "Option Style",
            "Symbol"
        ];

        // Main template sheet (headers only)
        const templateSheet = XLSX.utils.aoa_to_sheet([requiredHeaders]);

        // Set column widths to fit header text
        templateSheet['!cols'] = requiredHeaders.map(header => ({
            wch: Math.max(header.length + 2, 12) // +2 for padding, min width 12
        }));

        // Commodities tab
        const commoditiesSheet = XLSX.utils.json_to_sheet(
            (customerData.commodities ?? []).map(c => ({
                CommodityId: c.commodityId,
                CommodityName: c.commodityName
            }))
        );

        // Locations tab (if enabled)
        let locationsSheet: XLSX.WorkSheet | undefined;
        if (customerSettings.positionsByLocation) {
            locationsSheet = XLSX.utils.json_to_sheet(
                (customerData.locations ?? []).map(l => ({
                    LocationId: l.locationId,
                    LocationName: l.locationName
                }))
            );
        }

        // Counterparties tab (if enabled)
        let counterpartiesSheet: XLSX.WorkSheet | undefined;
        if (customerSettings.positionsByCounterparty) {
            counterpartiesSheet = XLSX.utils.json_to_sheet(
                (customerData.counterparties ?? []).map(c => ({
                    CounterpartyId: c.counterpartyId,
                    CounterpartyName: c.counterpartyName
                }))
            );
        }

        // Counterparties tab (if enabled)
        let portfoliosSheet: XLSX.WorkSheet | undefined;
        if (customerSettings.positionsByLocation) {
            portfoliosSheet = XLSX.utils.json_to_sheet(
                (customerData.portfolios ?? []).map(c => ({
                    PortfolioId: c.portfolioId,
                    PortfolioName: c.portfolioName
                }))
            );
        }

        // Enum values worksheet
        const enumSheetData = [
            ["Enum", "Value"],
            ...Object.entries({
                Side: ["Buy", "Sell"],
                PositionType: ["AllIn", "Future", "Option", "Physical", "Underlying", "Basis", "Swap", "Index"],
                OptionType: ["Call", "Put"],
                OptionStyle: ["American", "European", "Asian"],
                Unit: [
                    "None", "MMBTu", "MW", "Ton", "Tonne", "Bbl", "Bu", "Gal", "Lb", "Cwt", "Sc", "Contract", "Kg", "Lt", "MWh", "Toz", "Gj", "KgMs", "Allowance"
                ],
                Currency: ["USD", "CAD", "EUR", "AUD", "FX", "GBP", "BRL", "NZD", "MXN"]
            }).flatMap(([enumName, values]) =>
                (values as string[]).map(v => [enumName, v])
            )
        ];
        const enumsSheet = XLSX.utils.aoa_to_sheet(enumSheetData);

        // Build workbook
        const sheets: { [name: string]: XLSX.WorkSheet } = {
            "Positions Template": templateSheet,
            "Commodities": commoditiesSheet
        };
        if (locationsSheet) sheets["Locations"] = locationsSheet;
        if (counterpartiesSheet) sheets["Counterparties"] = counterpartiesSheet;
        if (portfoliosSheet) sheets["Portfolios"] = portfoliosSheet;
        sheets["Enums Reference"] = enumsSheet;

        const workbook: XLSX.WorkBook = {
            SheetNames: Object.keys(sheets),
            Sheets: sheets
        };

        // Write and trigger download
        const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array", cellStyles: true });
        const blob = new Blob([wbout], { type: "application/octet-stream" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "PositionsTemplate.xlsx");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }

    const progressPercentage = uploadProgress.total > 0 
        ? (uploadProgress.current / uploadProgress.total) * 100 
        : 0;

    const getProgressColor = () => {
        switch (uploadProgress.status) {
            case 'complete': return 'success';
            case 'error': return 'error';
            default: return 'primary';
        }
    };

    return (
        <div style={{ borderTop: '1px solid var(--gray5)', borderBottom: '1px solid var(--gray5)' }}>
            <StoneXRow align="center" marginDirection="topAndBottom">
                <IconButton onClick={() => setExpanded((previous) => !previous)}>
                    <ChevronRight sx={{ transform: expanded ? 'rotate(90deg)' : undefined }} />
                </IconButton>
                <h3 style={{ color: 'var(--gray2)' }}>Upload Positions</h3>
            </StoneXRow>

            {expanded && <>
                <div style={{ fontSize: 'small' }}>
                    <Button variant='text' onClick={downloadTemplate}>Download position template</Button>
                    (* indicates required field.)
                </div>
                <StoneXRow>
                    <StoneXFileUploader 
                        message="Upload positions" 
                        onUpload={getPositionsFromUpload}
                    />
                </StoneXRow>

                {/* Progress Bar */}
                {uploadProgress.status !== 'idle' && (
                    <Box sx={{ width: '100%', mt: 2, mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Box sx={{ width: '100%', mr: 1 }}>
                                <LinearProgress 
                                    variant={uploadProgress.status === 'validating' ? 'indeterminate' : 'determinate'}
                                    value={progressPercentage}
                                    color={getProgressColor()}
                                    sx={{ height: 10, borderRadius: 5 }}
                                />
                            </Box>
                            {uploadProgress.status === 'uploading' && (
                                <Box sx={{ minWidth: 50 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {Math.round(progressPercentage)}%
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                        <Typography 
                            variant="body2" 
                            color={uploadProgress.status === 'error' ? 'error' : 'text.secondary'}
                            sx={{ 
                                fontWeight: uploadProgress.status === 'complete' ? 'bold' : 'normal',
                                color: uploadProgress.status === 'complete' ? 'success.main' : undefined
                            }}
                        >
                            {uploadProgress.message}
                        </Typography>
                    </Box>
                )}

                {/* Table for invalid commodities with row numbers */}
                {invalidCommoditiesWithRow.length > 0 && (
                    <div style={{ margin: '16px 0' }}>
                        <h4 style={{ color: 'red' }}>Invalid Entries in Upload</h4>
                        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                            <thead>
                                <tr>
                                    <th style={{ border: '1px solid #ccc', padding: '4px 8px', background: '#f8d7da', color: '#721c24' }}>Type</th>
                                    <th style={{ border: '1px solid #ccc', padding: '4px 8px', background: '#f8d7da', color: '#721c24' }}>Name</th>
                                    <th style={{ border: '1px solid #ccc', padding: '4px 8px', background: '#f8d7da', color: '#721c24' }}>Row Number</th>
                                </tr>
                            </thead>
                            <tbody>
                                {invalidCommoditiesWithRow.map(({ type, name, row }, idx) => (
                                    <tr key={name + '_' + row + '_' + idx}>
                                        <td style={{ border: '1px solid #ccc', padding: '4px 8px' }}>{type}</td>
                                        <td style={{ border: '1px solid #ccc', padding: '4px 8px' }}>{name}</td>
                                        <td style={{ border: '1px solid #ccc', padding: '4px 8px' }}>{row}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </>}
        </div>
    );
}
